import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaServicestack, FaComments, FaBlog, FaCog, FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: var(--secondary-50);
`;

const Header = styled.header`
  background: white;
  padding: var(--space-lg) var(--space-xl);
  border-bottom: 1px solid var(--secondary-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  font-size: 1.5rem;
  color: var(--primary-600);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-md);
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  background: var(--error-600);
  color: white;
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    background: var(--error-700);
  }
`;

const Content = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-3xl) var(--space-xl);
`;

const Welcome = styled.div`
  margin-bottom: var(--space-3xl);
  
  h2 {
    font-size: 2rem;
    margin-bottom: var(--space-sm);
    color: var(--secondary-900);
  }
  
  p {
    color: var(--secondary-600);
    font-size: 1.125rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-xl);
`;

const Card = styled(Link)`
  background: white;
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  text-decoration: none;
  color: inherit;
  transition: all var(--transition-normal);
  border: 1px solid var(--secondary-200);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-lg);
    border-color: var(--primary-200);
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--primary-100);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-lg);
  
  svg {
    font-size: 1.5rem;
    color: var(--primary-600);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: var(--space-sm);
  color: var(--secondary-900);
`;

const CardDescription = styled.p`
  color: var(--secondary-600);
  line-height: 1.5;
`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();

  const dashboardItems = [
    {
      title: 'Services',
      description: 'Manage your coaching services and offerings',
      icon: <FaServicestack />,
      path: '/admin/services'
    },
    {
      title: 'Testimonials', 
      description: 'Manage client testimonials and reviews',
      icon: <FaComments />,
      path: '/admin/testimonials'
    },
    {
      title: 'Blog Posts',
      description: 'Create and manage blog content',
      icon: <FaBlog />,
      path: '/admin/blog'
    },
    {
      title: 'Content',
      description: 'Manage website content and settings',
      icon: <FaCog />,
      path: '/admin/content'
    }
  ];

  return (
    <Container>
      <Header>
        <Logo>Admin Dashboard</Logo>
        <UserInfo>
          <span>Welcome, {user?.name}</span>
          <LogoutButton onClick={logout}>
            <FaSignOutAlt />
            Logout
          </LogoutButton>
        </UserInfo>
      </Header>

      <Content>
        <Welcome>
          <h2>Dashboard</h2>
          <p>Manage your Scrum Master website content and settings</p>
        </Welcome>

        <Grid>
          {dashboardItems.map((item) => (
            <Card key={item.path} to={item.path}>
              <CardIcon>{item.icon}</CardIcon>
              <CardTitle>{item.title}</CardTitle>
              <CardDescription>{item.description}</CardDescription>
            </Card>
          ))}
        </Grid>
      </Content>
    </Container>
  );
};

export default AdminDashboard;